import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSubmissionHeatmapData,
  selectSubmissionHeatmapLoading,
} from '../../slices/reportsSlice';
import DNComplianceNotice from './DNComplianceNotice';
import SubmissionHeatmap from '../SubmissionHeatmap';

/**
 * Empty-state shown when the forecaster has no MarketMonthlyStats record for
 * the selected period — typically because their D+N submissions failed
 * Rule 7's strict per-challenge compliance and the aggregation produced only
 * penalty rows.
 *
 * Rule 7 compliance is tracked per (target_day, horizon_offset), so the
 * heatmap is per-horizon. The picker lists every horizon for which the
 * resource has at least one challenge in the period; the default is D+1
 * (the most common D+N case), falling back to the lowest available horizon
 * when D+1 isn't run on the resource.
 *
 * Falls back to a generic "no data" message when the heatmap has no
 * sessions at all (period truly empty / before user joined / etc.).
 */
const DEFAULT_HORIZON = 1;
const HORIZON_LABELS = {
  0: 'Intraday',
};

function formatHorizonLabel(horizon) {
  if (HORIZON_LABELS[horizon] !== undefined) return HORIZON_LABELS[horizon];
  return `D+${horizon}`;
}

function pickInitialHorizon(initialHorizon, availableHorizons) {
  if (availableHorizons.includes(initialHorizon)) return initialHorizon;
  if (availableHorizons.includes(DEFAULT_HORIZON)) return DEFAULT_HORIZON;
  // Lowest D+N (≥1) if D+1 isn't there; otherwise fall back to lowest overall.
  const dn = availableHorizons.find((h) => h >= 1);
  return dn ?? availableHorizons[0] ?? DEFAULT_HORIZON;
}

export default function DNComplianceEmptyState({
  resourceId,
  year,
  month,
  userId,
  initialHorizon = null,
}) {
  const heatmapData = useSelector(selectSubmissionHeatmapData);
  const heatmapLoading = useSelector(selectSubmissionHeatmapLoading);

  // The heatmap thunk is fired by the inner SubmissionHeatmap. The first
  // call may not include a horizon (when the parent hasn't selected one
  // yet) — in that case the backend returns the union of all horizons,
  // and `available_horizons` lets us pick a sensible default.
  const availableHorizons = useMemo(() => {
    const list = heatmapData?.available_horizons;
    return Array.isArray(list) ? list : [];
  }, [heatmapData]);

  const [chosenHorizon, setChosenHorizon] = useState(initialHorizon);

  // Once available_horizons resolves, lock chosenHorizon to a sensible
  // default (parent's selection if valid → D+1 → lowest D+N → lowest).
  useEffect(() => {
    if (availableHorizons.length === 0) return;
    setChosenHorizon((prev) => {
      if (prev != null && availableHorizons.includes(prev)) return prev;
      return pickInitialHorizon(initialHorizon, availableHorizons);
    });
  }, [availableHorizons, initialHorizon]);

  const { submitted, total } = useMemo(() => {
    const sessions = Array.isArray(heatmapData?.sessions)
      ? heatmapData.sessions
      : [];
    return {
      submitted: sessions.filter((s) => s.submitted).length,
      total: sessions.length,
    };
  }, [heatmapData]);

  // Wait until at least one heatmap fetch has resolved before deciding
  // whether to show the compliance variant or the generic empty state.
  if (heatmapLoading && total === 0 && availableHorizons.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-surface p-8 dark:border-gray-700 dark:bg-gray-870">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Loading submission data…
        </p>
      </div>
    );
  }

  // No challenges at all for this period — nothing meaningful to show.
  if (availableHorizons.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-surface p-8 dark:border-gray-700 dark:bg-gray-870">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No analytics data is available for the selected period yet.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-surface p-6 dark:border-gray-700 dark:bg-gray-870">
      <DNComplianceNotice
        submitted={submitted}
        total={total}
        horizonLabel={chosenHorizon != null ? formatHorizonLabel(chosenHorizon) : null}
      />

      {availableHorizons.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Forecast horizon
          </span>
          {availableHorizons.map((h) => {
            const active = h === chosenHorizon;
            return (
              <button
                key={h}
                type="button"
                onClick={() => setChosenHorizon(h)}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-all ${
                  active
                    ? 'border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-600 dark:bg-brand-900/30 dark:text-brand-300'
                    : 'border-gray-200 text-gray-600 hover:border-brand-300 dark:border-gray-700 dark:text-gray-400'
                }`}
              >
                {formatHorizonLabel(h)}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-2">
        <SubmissionHeatmap
          resourceId={resourceId}
          year={year}
          month={month}
          selectedHorizon={chosenHorizon}
          userId={userId}
        />
      </div>
    </div>
  );
}
